import React, { useContext, useReducer } from "react"
import { CarrotErrorCodes } from "#/utils/CarrotErrors"
import { reportError } from "#/utils/ErrorReporting"
import { isHttpError } from "#/utils/HttpErrors"
import { useSelector } from "react-redux"
import { loginEmail } from "#/redux/reducers/loginInfo"
import { useSignUpFlow } from "../../context/signup/SignUpFlowContext"
import { useHistory } from "react-router"
import { useIntl } from "react-intl"
import { useTrackExperimentEvent } from "../../views/register/hooks/useExperiments"
import { carrotClient } from "#/utils/CarrotClient"

interface SignUpReducerState {
  username: string
  errorMessage: string
  inputsEnabled: boolean
  showSubmittingLabel: boolean
  success: boolean
  shake: boolean
}

type SignUpReducerAction = {
  type: string
  payload?: { errorCode: string }
}

interface SignUpProviderProps extends SignUpReducerState {
  onSubmit: ({ email }: { email: any }) => Promise<void>
}

const SignUpContext = React.createContext<SignUpProviderProps>(null)

export const useSignUp = () => useContext(SignUpContext)

const actionTypes = {
  error: "ERROR",
  start: "REQUEST_START",
  success: "REQUEST_SUCCESS"
}

function signUpReducer(state: SignUpReducerState, action: SignUpReducerAction): SignUpReducerState {
  switch (action.type) {
    case actionTypes.error: {
      return {
        ...state,
        inputsEnabled: true,
        showSubmittingLabel: false,
        shake: true,
        errorMessage: action.payload.errorCode
      }
    }
    case actionTypes.start: {
      return {
        ...state,
        inputsEnabled: true,
        showSubmittingLabel: true,
        shake: false
      }
    }
    case actionTypes.success: {
      return {
        ...state,
        inputsEnabled: false,
        showSubmittingLabel: false,
        success: true
      }
    }
    default: {
      throw new Error(`Unhandled type: ${action.type}`)
    }
  }
}

export function SignUpProvider({ children }: { children: React.ReactNode | React.ReactNode[] }) {
  const username = useSelector(loginEmail)
  const [state, dispatch] = useReducer(signUpReducer, {
    username,
    errorMessage: null,
    inputsEnabled: true,
    showSubmittingLabel: false,
    success: false,
    shake: false
  })
  const { handleSaveMemberInfoInMemory, lookupAccountByEmail } = useSignUpFlow()
  const history = useHistory()
  const intl = useIntl()
  const { locale } = intl
  const { trackExperimentEvent } = useTrackExperimentEvent()

  const setFormError = (errorCode: string): void => dispatch({ type: actionTypes.error, payload: { errorCode } })
  const startRequest = () => dispatch({ type: actionTypes.start })
  const endSuccessfulRequest = () => dispatch({ type: actionTypes.success })

  const redirectToNameDobSignUp = (email: string): void => {
    history.push(`/signup-find-account?email=${encodeURIComponent(email)}`)
  }

  const setErrorState = (errorCode: string): void => {
    setFormError(errorCode)
  }

  const onSubmit = async ({ email }: { email: string }): Promise<void> => {
    trackExperimentEvent("sign-up-continue")
    startRequest()

    let shouldSignUp = true
    try {
      const lookupResponseStatus = await lookupAccountByEmail(email)
      if (lookupResponseStatus === undefined) {
        shouldSignUp = false
        return
      }

      if (lookupResponseStatus != null) {
        setFormError(lookupResponseStatus)
        shouldSignUp = false
      }

      shouldSignUp && (await carrotClient.signUp(email, locale))
    } catch (error) {
      if (isHttpError(error)) {
        const { code } = await error.response.json()
        if (code === CarrotErrorCodes.EMPLOYEE_NOT_FOUND) {
          handleSaveMemberInfoInMemory({ email })
          redirectToNameDobSignUp(email)
          return
        }

        setErrorState(code)
        return
      }

      reportError(error)
      setErrorState(CarrotErrorCodes.UNEXPECTED_ERROR)
      return
    }
    if (shouldSignUp) {
      endSuccessfulRequest()
      const signupSuccessHref = `/signup-success?email=${encodeURIComponent(email)}`
      history.push(signupSuccessHref)
    }
  }

  return <SignUpContext.Provider value={{ ...state, onSubmit }}>{children}</SignUpContext.Provider>
}
